import React, { useState, useEffect } from 'react';
import { BookOpen, Clock, ChevronRight, Play, Loader } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { egwEnrollmentService } from '../../../services/egwPlanService';

const EGWPlanCard = ({ plan, enrollment, onEnroll, onContinue, enrolling }) => {
  const [pace, setPace] = useState(plan?.default_pace || 3);
  const isEnrolled = !!enrollment;
  const progress = isEnrolled
    ? Math.round(((enrollment?.current_day - 1) / Math.max(plan?.total_days, 1)) * 100)
    : 0;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-warm transition-all duration-200">
      {/* Cover */}
      <div className="h-32 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center relative">
        {plan?.cover_image_url ? (
          <img src={plan?.cover_image_url} alt={plan?.title} className="w-full h-full object-cover" />
        ) : (
          <BookOpen size={40} className="text-primary/40" />
        )}
        {plan?.series && (
          <span className="absolute top-2 left-2 text-xs bg-black/50 text-white px-2 py-0.5 rounded-full">
            {plan?.series}
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-heading font-semibold text-foreground mb-1 line-clamp-2">{plan?.title}</h3>
        <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{plan?.description}</p>

        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
          <span className="flex items-center gap-1">
            <Clock size={12} />
            {plan?.total_days} days
          </span>
          <span>{plan?.book_code}</span>
          <span>{plan?.total_pages} pages</span>
        </div>

        {isEnrolled ? (
          <>
            {/* Progress bar */}
            <div className="mb-3">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Day {enrollment?.current_day} of {plan?.total_days}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Pace slider */}
            <div className="mb-3">
              <label className="text-xs text-muted-foreground">Pace: {enrollment?.pace_pages_per_day} pages/day</label>
              <input
                type="range"
                min={1}
                max={10}
                value={enrollment?.pace_pages_per_day}
                onChange={async (e) => {
                  const newPace = parseInt(e?.target?.value);
                  await egwEnrollmentService?.updatePace(enrollment?.id, newPace);
                }}
                className="w-full h-1.5 accent-primary mt-1"
              />
            </div>

            <Button onClick={() => onContinue(enrollment, plan)} className="w-full flex items-center justify-center gap-2">
              <Play size={14} />
              Continue Reading
            </Button>
          </>
        ) : (
          <>
            <div className="mb-3">
              <label className="text-xs text-muted-foreground">Pace: {pace} pages/day</label>
              <input
                type="range"
                min={1}
                max={10}
                value={pace}
                onChange={(e) => setPace(parseInt(e?.target?.value))}
                className="w-full h-1.5 accent-primary mt-1"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => onEnroll(plan, pace)}
              disabled={enrolling}
              className="w-full flex items-center justify-center gap-2"
            >
              {enrolling ? <Loader size={14} className="animate-spin" /> : <ChevronRight size={14} />}
              Enroll
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default EGWPlanCard;
